import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { loginRequest, msalInstance } from './utils/msalConfig'; // Ensure you have your MSAL config defined
import { acquireTokenSilently } from './utils/tokenRefresh';
import { BACKEND_API_URL } from '../config';

const initialState = {
  token: null,
  // Epoch ms when `token` expires — drives the silent background renewal.
  tokenExpiresOn: null,
  role: null,
  permissions: [],
  name: null,
  // Profile image URL (`photoLink` from /api/admin/permissions); the header falls
  // back to an initial avatar when it is missing or fails to load.
  photo: null,
  isAuthenticated: false,
  data: [],
  profiles: [],
  error: null,
  loading: false,
};

// Async thunk for acquiring token. Silent — MSAL uses its cached refresh token;
// if that is dead too, acquireTokenSilently redirects to Microsoft sign-in.
export const acquireToken = createAsyncThunk(
  'admin/acquireToken',
  async () => {
    const result = await acquireTokenSilently({ redirectOnFailure: true });
    return {
      accessToken: result?.accessToken ?? null,
      expiresOn: result?.expiresOn ?? null,
    };
  }
);


export const login = createAsyncThunk(
    'admin/login',
    async (_, { getState }) => {
       try{
            const response =  await msalInstance.loginRedirect(loginRequest);
            return { accessToken: response.accessToken }
       }
       catch {
           return { accessToken: null }
       }
    }
  );
  
export const checkAuthentication = createAsyncThunk(
    'admin/checkAuthentication',
    async (_, { getState }) => {
      return msalInstance.getAllAccounts().length > 0;
    }
);


export const logout = createAsyncThunk(
    'admin/logout',
    async (_, { rejectWithValue }) => {
      try {
        await msalInstance.logoutPopup({
          postLogoutRedirectUri: "/login"
        });
        return {}; // Assuming logout is always successful
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
  );


// **************************  for all admin roles **************************

// Fetch User/Admin permissions, 
export const fetchAdminPermissions = createAsyncThunk(
  'admin/fetchAdminPermissions',
  async (_, { getState, rejectWithValue }) => {
    try {
      const accessToken = getState().admin?.token;
      const response = await fetch(`${BACKEND_API_URL}/api/admin/permissions`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        return rejectWithValue('Network response was not ok');
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue('Error fetching admin permissions');
    }
  }
)

// Fetch all onboarding profiles for review 
// ALL profiles will be fetched with HR team reviews if available
export const fetchAllOnboardingProfiles = createAsyncThunk(
  'admin/fetchAllOnboardingProfiles',
  async (data, { getState, rejectWithValue }) => {
    const accessToken = getState().admin?.token;
    try {
      const response = await fetch(`${BACKEND_API_URL}/api/admin/all-onboarding-profiles`, {
          headers: new Headers({
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
          }),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue('Error fetching data');
    }
  }
);


// ************************************************************************

// ***************** Employee Related ************************************************
export const fetchMyLinks = createAsyncThunk(
    'admin/fetchMyLinks',
    async (data, { getState, rejectWithValue }) => {
      const accessToken = getState().admin?.token;

      try {
        const response = await fetch(`${BACKEND_API_URL}/api/admin/access-token`, {
            headers: new Headers({
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }),
        });

        if (!response.ok) {
          return rejectWithValue('Error fetching data');
        }

        const data = await response.json();

        // Endpoint is serialized with many=True, so a non-array means an error body
        // slipped through — storing it would break the links table render.
        if (!Array.isArray(data)) {
          return rejectWithValue('Error fetching data');
        }

        return data;
      } catch (error) {
        return rejectWithValue('Error fetching data');
      }
    }
  );

export const fetchEmployeeOnboardingProfiles = createAsyncThunk(
    'admin/fetchEmployeeOnboardingProfiles',
    async (data, { getState, rejectWithValue }) => {
      const accessToken = getState().admin?.token;
      try {
        const response = await fetch(`${BACKEND_API_URL}/api/admin/employee-onboarding-profiles`, {
            headers: new Headers({
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }),
        });
        const data = await response.json();
        return data;
      } catch (error) {
        return rejectWithValue('Error fetching data');
      }
    }
  );

/**
 * The link generation API validates the whole payload and answers a failure with
 * DRF field errors ({field: [message]}, nested for manager / co_managers).
 * Flatten them into one readable message so the error is shown instead of being
 * treated as a created link.
 */
const flattenApiErrors = (payload, path = '') => {
    if (payload === null || payload === undefined) return [];
    if (typeof payload !== 'object') return [path ? `${path}: ${payload}` : `${payload}`];
    if (Array.isArray(payload)) return payload.flatMap(item => flattenApiErrors(item, path));
    return Object.entries(payload).flatMap(([key, value]) =>
        flattenApiErrors(value, path ? `${path}.${key}` : key)
    );
};

const apiErrorMessage = (payload, fallback) => {
    if (!payload || typeof payload !== 'object') {
        return flattenApiErrors(payload).join('\n') || fallback;
    }
    // The API repeats the field errors under `errors`, so read them from there and
    // leave the status flag out of the message.
    const { success, message, errors, ...fields } = payload;
    const details = flattenApiErrors(errors ?? fields);
    const lines = [...new Set([message, ...details].filter(Boolean))];
    return lines.length ? lines.join('\n') : fallback;
};

export const generateLink = createAsyncThunk(
    'admin/generateLink',
    async (data, {getState, rejectWithValue}) => {
        const accessToken = getState().admin?.token;
        try {
            const response = await fetch(`${BACKEND_API_URL}/api/admin/access-token`, {
                method: 'POST',
                headers: new Headers({
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }),
                body: JSON.stringify(data),
            });
            const response_data = await response.json();
            if (!response.ok) {
                return rejectWithValue(
                    apiErrorMessage(response_data, 'Could not generate the link. Please try again.')
                );
            }
            return response_data;
        } catch (error) {
          return rejectWithValue('Error fetching data');
        }
    }
)

export const deleteLink = createAsyncThunk(
  'admin/deleteLink',
  async (data, {getState, rejectWithValue}) => {
      const accessToken = getState().admin?.token;
      try {
          const response = await fetch(`${BACKEND_API_URL}/api/admin/access-token`, {
              method: 'DELETE',
              headers: new Headers({
                  'Authorization': `Bearer ${accessToken}`,
                  'Content-Type': 'application/json'
              }),
              body: JSON.stringify(data),
          });
          const response_data = await response.json();
          return response_data;
      } catch (error) {
        return rejectWithValue('Error fetching data');
      }
  }
)


export const resendLink = createAsyncThunk(
  'admin/resendLink',
  async (data, {getState, rejectWithValue}) => {
      const accessToken = getState().admin?.token;
      let expiryDate = new Date(data.expiry_time);
      
      // Get the current date
      let currentDate = new Date();

      // Check if the expiry date is in the past
      if (currentDate > expiryDate) {
          alert("The link has expired.");
          return;
      }

      try {
          const response = await fetch(`${BACKEND_API_URL}/api/admin/resend-generated-link`, {
              method: 'POST',
              headers: new Headers({
                  'Authorization': `Bearer ${accessToken}`,
                  'Content-Type': 'application/json'
              }),
              body: JSON.stringify(data),
          });
          const response_data = await response.json();
          return response_data;
      } catch (error) {
        return rejectWithValue('Error fetching data');
      }
  }
)

// *****************************************************************************************


// ******************* ACCOUNTING Related ***********************************
// ALL profiles will be fetched with accounting team reviews if available
// Removed on 27th Nov, 2024

// ******************* HR Related ***********************************
// ALL profiles will be fetched with Accounting & HR team reviews if available

export const submitHRTeamReview = createAsyncThunk(
  'admin/submitHRTeamReview',
  async (data, {getState, rejectWithValue}) => {
      const accessToken = getState().admin?.token;
      try {
          const response = await fetch(`${BACKEND_API_URL}/api/admin/hr/review`, {
              method: 'POST',
              headers: new Headers({
                  'Authorization': `Bearer ${accessToken}`,
                  'Content-Type': 'application/json'
              }),
              body: JSON.stringify(data),
          });
          const response_data = await response.json();
          return response_data;
      } catch (error) {
        return rejectWithValue('Error fetching data');
      }
  }
)

export const downloadProfileReport = createAsyncThunk(
  'admin/downloadProfileReport',
    async (id, { getState, rejectWithValue }) => {
      const accessToken = getState().admin?.token;
      try {
        const response = await fetch(`${BACKEND_API_URL}/api/onboarding/report/${id}`, {
          headers: new Headers({
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || 'Download failed');
        }

        const blob = await response.blob();
        return blob;
      } catch (err) {
        return rejectWithValue(err.message || 'Download failed');
      }
    }
);


// *******************************************************************************
const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    // Background renewal only records the new expiry (to schedule the next one).
    // `token` is intentionally left as-is — many effects refetch when it changes;
    // the fetch layer sends the newest token instead (utils/tokenRefresh.js).
    tokenRenewed: (state, action) => {
      if (action.payload?.expiresOn) state.tokenExpiresOn = action.payload.expiresOn;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(acquireToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(acquireToken.fulfilled, (state, action) => {
        // state.loading = false;
        state.token = action.payload.accessToken;
        state.tokenExpiresOn = action.payload.expiresOn;
        state.isAuthenticated = true;
      })
      .addCase(acquireToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.isAuthenticated = false;
      })
      .addCase(checkAuthentication.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkAuthentication.fulfilled, (state, action) => {
        // state.loading = false;
        state.isAuthenticated = action.payload;
      })
      .addCase(checkAuthentication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.accessToken) state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state, action) => {
        state.token = null;
        state.isAuthenticated = false;
      })
      .addCase(logout.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchMyLinks.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMyLinks.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchMyLinks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchEmployeeOnboardingProfiles.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEmployeeOnboardingProfiles.fulfilled, (state, action) => {
        state.loading = false;
        state.profiles = action.payload;
      })
      .addCase(fetchEmployeeOnboardingProfiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(generateLink.pending, (state) => {
        state.loading = true;
      })
      .addCase(generateLink.fulfilled, (state, action) => {
        state.loading = false;
        state.data.unshift(action.payload);
      })
      .addCase(generateLink.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteLink.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteLink.fulfilled, (state, action) => {
        state.loading = false;
        let index = state.data.findIndex(obj => obj.id === action.payload?.data?.id);
            // Delete the object if it exists
            if (index !== -1) {
                state.data.splice(index, 1);
            }
      })
      .addCase(deleteLink.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(resendLink.pending, (state) => {
        state.loading = true;
      })
      .addCase(resendLink.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(resendLink.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // *****
      .addCase(fetchAdminPermissions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAdminPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.role = action.payload?.roles[0] || null;
        state.permissions = action.payload?.permissions;
        state.name = action.payload?.name || null;
        state.photo = action.payload?.photoLink || null;
      })
      .addCase(fetchAdminPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllOnboardingProfiles.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllOnboardingProfiles.fulfilled, (state, action) => {
        state.loading = false;
        state.profiles = action.payload;
      })
      .addCase(fetchAllOnboardingProfiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // HR
      .addCase(submitHRTeamReview.pending, (state) => {
        state.loading = true;
      })
      .addCase(submitHRTeamReview.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(submitHRTeamReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Download profile report
      .addCase(downloadProfileReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(downloadProfileReport.fulfilled, (state, action) => {
        state.loading = false;
        // Blob payload is consumed straight from the thunk result by the download
        // components, so it must not overwrite the links list held in state.data.
      })
      .addCase(downloadProfileReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { tokenRenewed } = adminSlice.actions;

export default adminSlice.reducer;
